import type * as ents from '@attain-apps/entities'
import { TypeChecker } from '@attain-apps/type-guards'
import type * as chartJs from 'chart.js'

import * as color from '../../../../../color'
import * as utils from '../utils'

/**
 * Transform data from
 * {@link ents.core.display.Chart.Options<ents.core.display.chart.Type.LineChart>} to
 * {@link chartJs.ChartOptions<'line'>}
 */
export function getOptions({
  config,
  data,
}: ents.core.display.chart.Chart<ents.core.display.chart.Type.Line>): chartJs.ChartOptions<'line'> {
  let options: chartJs.ChartOptions<'line'> = {
    ...utils.DEFAULT_OPTIONS,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        displayColors: data?.values
          ? utils.getUniqueDatasetNames(data.values).size > 1
          : false,
      },
    },
    scales: {
      x: {
        // NOTE: checking if every value is type of number, to set type config
        // to linear. 'linear' scale is used, because for linear scale, the
        // spacing between values is proportional to the actual differences
        // between the numbers.
        type: utils.hasNumericXValues(data?.values) ? 'linear' : 'category',
      },
      y: {
        ticks: {
          callback: function (value) {
            if (TypeChecker.isNumber(value) && value >= 1000000) {
              return value / 1000000 + 'M'
            }
            return value
          },
        },
      },
    },
  }

  /**
   * Will be equal to {@link config?.name} if available; otherwise, it defaults to
   * {@link data.name}. If neither is defined, `undefined` is assigned.
   */
  const chartTitle = config?.name ?? data?.name
  if (chartTitle) {
    options = {
      ...options,
      plugins: {
        ...options.plugins,
        title: {
          align: 'center',
          color: color.BLACK,
          display: true,
          font: {
            weight: 'bold',
          },
          padding: {
            bottom: 20,
          },
          text: chartTitle,
        },
      },
    }
  }

  if (config) {
    const { onItemClick } = config
    if (data && onItemClick) {
      options.onClick = (_, elements) => {
        const point = utils.getPointByIndex(data, elements)
        if (point) {
          onItemClick(point)
        }
      }
    }
  }

  return options
}
